import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import './Tours.css';

const Tours = () => {
  const [tours, setTours] = useState([]);
  const [filteredTours, setFilteredTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [togglingStatus, setTogglingStatus] = useState({});
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchTours();
  }, []);

  const fetchTours = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/tours`);
      setTours(response.data.data || []);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch tours');
      setLoading(false);
    }
  };

  const applyFilters = useCallback(() => {
    let filtered = [...tours];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(tour =>
        (tour.title && tour.title.toLowerCase().includes(query)) ||
        (tour.city && tour.city.toLowerCase().includes(query))
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(tour => tour.status === statusFilter);
    }
    if (cityFilter !== 'all') {
      filtered = filtered.filter(tour => tour.city === cityFilter);
    }
    setFilteredTours(filtered);
  }, [tours, searchQuery, statusFilter, cityFilter]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const getUniqueCities = () => {
    const cities = [...new Set(tours.map(t => t.city).filter(Boolean))];
    return cities.sort();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setCityFilter('all');
  };

  const hasActiveFilters = () => {
    return searchQuery.trim() !== '' || statusFilter !== 'all' || cityFilter !== 'all';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tour?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/admin/tours/${id}`);
      setTours(tours.filter(t => t._id !== id));
    } catch (err) {
      alert('Failed to delete tour');
    }
  };

  const handleToggleStatus = async (tourId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    setTogglingStatus(prev => ({ ...prev, [tourId]: true }));
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/tours/${tourId}`, {
        status: newStatus
      }, { headers: { 'Content-Type': 'application/json' } });
      setTours(tours.map(t =>
        t._id === tourId ? { ...t, status: response.data.data.status } : t
      ));
    } catch (err) {
      alert('Failed to update tour status');
    } finally {
      setTogglingStatus(prev => ({ ...prev, [tourId]: false }));
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${API_BASE_URL_WITHOUT_API}${imagePath}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="tours-container">
          <div className="tours-header">
            <div className="tours-header-content">
              <h1>Tours & Activities</h1>
              <p className="tours-subtitle">Manage tour and activity listings</p>
            </div>
            <Link to="/sy-admin/tours/new" className="btn btn-primary btn-add">
              <span className="btn-icon">+</span>
              Add New Tour
            </Link>
          </div>
          <div className="tours-table-container">
            <table className="tours-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Title</th>
                  <th>City</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i} className="skeleton-row">
                    <td><div className="skeleton skeleton-image" style={{ width: '60px', height: '60px', borderRadius: '8px' }} /></td>
                    <td><div className="skeleton skeleton-text" style={{ width: '60%', height: '16px' }} /></td>
                    <td><div className="skeleton skeleton-text" style={{ width: '50%', height: '14px' }} /></td>
                    <td><div className="skeleton skeleton-text" style={{ width: '40%', height: '14px' }} /></td>
                    <td><div className="skeleton skeleton-switch" style={{ width: '80px', height: '26px' }} /></td>
                    <td><div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="tours-container">
        <div className="tours-header">
          <div className="tours-header-content">
            <h1>Tours & Activities</h1>
            <p className="tours-subtitle">Manage tour and activity listings</p>
          </div>
          <Link to="/sy-admin/tours/new" className="btn btn-primary btn-add">
            <span className="btn-icon">+</span>
            Add New Tour
          </Link>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {tours.length > 0 && (
          <div className="filters-section">
            <div className="filters-header">
              <button
                className="filters-toggle-btn"
                onClick={() => setShowFilters(!showFilters)}
              >
                <span className="filters-icon">🔍</span>
                Filters
                {hasActiveFilters() && (
                  <span className="active-filters-badge">
                    {[searchQuery.trim(), statusFilter, cityFilter].filter(v => v !== '' && v !== 'all').length}
                  </span>
                )}
                <span className="toggle-arrow">{showFilters ? '▲' : '▼'}</span>
              </button>
              {hasActiveFilters() && (
                <button className="clear-filters-btn" onClick={clearFilters}>Clear All</button>
              )}
            </div>
            {showFilters && (
              <div className="filters-content">
                <div className="filter-row">
                  <div className="filter-group">
                    <label className="filter-label">Search</label>
                    <input
                      type="text"
                      className="filter-input"
                      placeholder="Search by title or city..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="filter-group">
                    <label className="filter-label">Status</label>
                    <select
                      className="filter-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label className="filter-label">City</label>
                    <select
                      className="filter-select"
                      value={cityFilter}
                      onChange={(e) => setCityFilter(e.target.value)}
                    >
                      <option value="all">All Cities</option>
                      {getUniqueCities().map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {hasActiveFilters() && (
                  <div className="filter-results-info">
                    Showing {filteredTours.length} of {tours.length} tours
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tours.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🎫</div>
            <h2>No tours found</h2>
            <p>Get started by adding your first tour or activity.</p>
            <Link to="/sy-admin/tours/new" className="btn btn-primary">Add Your First Tour</Link>
          </div>
        ) : (
          <div className="tours-table-container">
            {filteredTours.length === 0 && hasActiveFilters() ? (
              <div className="empty-state">
                <div className="empty-state-icon">🔍</div>
                <h2>No tours match your filters</h2>
                <p>Try adjusting your filter criteria or clear all filters.</p>
                <button onClick={clearFilters} className="btn btn-primary">Clear Filters</button>
              </div>
            ) : (
              <table className="tours-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Title</th>
                    <th>City</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTours.map((tour) => {
                    const isTogglingStatus = togglingStatus[tour._id];
                    const featuredImage = tour.featuredImage || (tour.images && tour.images[0]);
                    const imageUrl = getImageUrl(featuredImage);
                    return (
                      <tr key={tour._id}>
                        <td>
                          <div className="tour-image-cell">
                            {imageUrl ? (
                              <img src={imageUrl} alt={tour.title} className="tour-table-image" />
                            ) : (
                              <div className="tour-image-placeholder">
                                <span className="placeholder-icon-small">🎫</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="tour-name-cell">
                            <span className="tour-name-text">{tour.title}</span>
                          </div>
                        </td>
                        <td>
                          <div className="tour-city-cell">
                            <span className="location-icon">📍</span>
                            <span>{tour.city || 'N/A'}</span>
                          </div>
                        </td>
                        <td>
                          <span className="tour-duration-cell">{tour.duration || '—'}</span>
                        </td>
                        <td>
                          <div className="status-cell">
                            <label className="status-toggle-label">
                              <div className="toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={tour.status === 'active'}
                                  onChange={() => handleToggleStatus(tour._id, tour.status)}
                                  disabled={isTogglingStatus}
                                  className="toggle-input"
                                />
                                <span className={`toggle-slider ${isTogglingStatus ? 'loading' : ''}`} />
                              </div>
                              <span className={`status-text ${tour.status === 'active' ? 'status-active-text' : 'status-inactive-text'}`}>
                                {tour.status === 'active' ? 'Active' : tour.status === 'inactive' ? 'Inactive' : 'Pending'}
                              </span>
                            </label>
                          </div>
                        </td>
                        <td>
                          <div className="table-actions">
                            <button
                              onClick={() => navigate(`/sy-admin/tours/edit/${tour._id}`)}
                              className="btn btn-edit btn-icon-only"
                              title="Edit Tour"
                            >
                              <span className="btn-icon">✏️</span>
                            </button>
                            <button
                              onClick={() => handleDelete(tour._id)}
                              className="btn btn-delete btn-icon-only"
                              title="Delete Tour"
                            >
                              <span className="btn-icon">🗑️</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Tours;
